import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../../../context/AuthContext";
import { logActivity } from "../activityLog";
import { isProduction } from "../utils/contour";
import { toast } from "react-toastify";

/**
 * WHAT A READER GETS WITHOUT PAYING.
 *
 * The dial lives here rather than in the app bundle because it is a product
 * decision that will be turned weekly until it finds its place: a constant in
 * the build means a deploy for every turn, and a change nobody can make without
 * an engineer. Saved here, it applies on the next request, to everyone.
 *
 * THE SERVER DECIDES, THIS SCREEN ONLY SETS THE NUMBER. The lock a reader sees
 * is drawn from the server's answer; the server reads this document when it
 * decides whether to hand over a lesson's text. A lock drawn by the browser is
 * a picture of a lock.
 *
 * AND THE SAFE DEFAULT IS "NOTHING OPEN". Absent, empty or unreadable — on the
 * server's side every one of them means nothing is free, never everything. So
 * an empty screen here is not a dangerous state: it is the closed one.
 *
 * TWO GRAINS, BECAUSE A WHOLE COURSE IS USUALLY TOO MUCH. Opening Foundations
 * hands over fourteen lessons at once, which is a large bet to place before
 * anything is known. A course can be ticked whole, and it can be opened lesson
 * by lesson; the server unions the two, so a lesson is free if either names it.
 * The count beside the heading is what the decision actually costs, and it is
 * counted, never typed.
 */

interface Lesson {
  slug: string;
  title: string;
}

interface Course {
  slug: string;
  title: string;
  order: number;
  lessons: Lesson[];
}

const FreeAccessSettings: React.FC = () => {
  const { project } = useParams<{ project: string }>();
  const { user, firebaseUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [pickedCourses, setPickedCourses] = useState<string[]>([]);
  const [pickedLessons, setPickedLessons] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);

  useEffect(() => {
    if (!project) return;
    setLoading(true);
    (async () => {
      try {
        const courseSnap = await getDocs(collection(db, "content", project, "courses"));
        const list: Course[] = [];
        for (const d of courseSnap.docs) {
          const data = d.data() as Record<string, unknown>;
          const slug = (data.slug as string) || d.id;
          const lessons: Lesson[] = [];
          try {
            const mods = await getDocs(
              collection(db, "content", project, "courses", slug, "versions", "v1", "modules")
            );
            const ordered = mods.docs.sort(
              (a, b) => Number(a.data().order ?? 0) - Number(b.data().order ?? 0)
            );
            for (const m of ordered) {
              const les = await getDocs(
                collection(
                  db, "content", project, "courses", slug, "versions", "v1", "modules", m.id, "lessons"
                )
              );
              const inOrder = les.docs.sort(
                (a, b) => Number(a.data().order ?? 0) - Number(b.data().order ?? 0)
              );
              for (const l of inOrder) {
                lessons.push({ slug: l.id, title: (l.data().title as string) || l.id });
              }
            }
          } catch (err) {
            // A course whose lessons cannot be listed is shown WITHOUT them
            // rather than as an empty course: "no lessons" and "could not read
            // the lessons" are different facts and must not look alike.
            console.error("[FreeAccessSettings] lessons unreadable for", slug, err);
          }
          list.push({
            slug,
            title: (data.title as string) || d.id,
            order: Number(data.order ?? 0),
            lessons,
          });
        }
        list.sort((a, b) => a.order - b.order);
        setCourses(list);

        const cfgSnap = await getDoc(doc(db, "content", project, "_config", "free_access"));
        const data = cfgSnap.exists() ? (cfgSnap.data() as Record<string, unknown>) : {};
        setPickedCourses(Array.isArray(data.courses) ? (data.courses as string[]) : []);
        setPickedLessons(Array.isArray(data.lessons) ? (data.lessons as string[]) : []);
      } catch (err) {
        console.error("[FreeAccessSettings] Load error:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [project]);

  /** Every lesson the current picks open, courses and named lessons unioned —
   *  the same rule the server applies, so the number shown is the number served. */
  const openSet = useMemo(() => {
    const open = new Set<string>();
    for (const c of courses) {
      if (pickedCourses.includes(c.slug)) for (const l of c.lessons) open.add(l.slug);
    }
    const known = new Set(courses.flatMap((c) => c.lessons.map((l) => l.slug)));
    for (const s of pickedLessons) if (known.has(s)) open.add(s);
    return open;
  }, [courses, pickedCourses, pickedLessons]);

  const totalCount = useMemo(
    () => courses.reduce((n, c) => n + c.lessons.length, 0),
    [courses]
  );

  const toggleCourse = (slug: string) =>
    setPickedCourses((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));

  const toggleLesson = (slug: string) =>
    setPickedLessons((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));

  const toggleExpand = (slug: string) =>
    setExpanded((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));

  const save = async () => {
    if (!project) return;
    /*
     * PRODUCTION IS ASKED ABOUT, STAGE IS NOT.
     *
     * This dial decides what a reader gets without paying, and on 13.10 it was
     * saved against production by somebody who believed he was on stage. The
     * switch in the tab bar now says which contour this is; this is the second
     * lock, on the one action that cannot be taken back by looking harder.
     *
     * The sentence names the NUMBER, not just the contour: "production" is a
     * word, "14 lessons open to everyone" is the consequence.
     */
    if (isProduction(project)) {
      const ok = confirm(
        `PRODUCTION — ${project}\n\n` +
          `Save will open ${openSet.size} of ${totalCount} lessons to everyone, ` +
          `with no payment, for real buyers.\n\nSave to production?`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await setDoc(
        doc(db, "content", project, "_config", "free_access"),
        {
          /*
           * `open` IS WHAT THE SERVER READS, and it is the only field that
           * decides anything. The union of "this whole course" and "these
           * lessons" is computed here, against the catalogue this screen has
           * just read, and stored resolved — so the server answers one
           * question with one small document instead of walking the catalogue
           * on every lesson open.
           *
           * THE OTHER TWO FIELDS ARE THIS SCREEN'S MEMORY OF THE PICKS. They
           * are what redraws the ticks when you come back; the server ignores
           * them. Keeping them is what makes "the whole course" still mean the
           * whole course after you reload.
           *
           * WHAT THIS COSTS, SAID PLAINLY: a lesson added to a ticked course
           * tomorrow is NOT free until this is saved again. The direction is
           * the safe one — a new lesson stays paid rather than being given
           * away — and the count in the heading above is where you see it.
           *
           * AND THE SAME UNION IS COMPUTED BY THE COMMAND-LINE TOOL in the
           * monorepo (apps/aiguru/tools/free-access.mjs), because the two live
           * in different repositories and cannot share the line. The server
           * counts the one disagreement it can see — courses ticked while
           * nothing is open — under `courses_unresolved`, so a writer that
           * forgets to resolve shows up as a number rather than as a lesson
           * quietly asking for money.
           */
          open: Array.from(openSet),
          courses: pickedCourses,
          lessons: pickedLessons,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        },
        { merge: true }
      );
      toast.success("Saved");
      logActivity(project, {
        action: "edit",
        contentType: "free_access",
        docTitle: "Free access",
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err) {
      console.error("[FreeAccessSettings] Save error:", err);
      toast.error("Save failed");
    }
    setSaving(false);
  };

  if (loading) return <div className="editor__loading">Loading...</div>;

  return (
    <div className="general">
      <div className="general__header">
        <h2 className="general__title">Free access</h2>
        <div className="general__actions">
          <button className="editor__save-btn" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="general__section">
        <h3 className="general__section-title">
          {/*
            * THE COST OF THE DECISION, IN THE HEADING. "Three courses" is not a
            * quantity anyone can judge; "14 of 87 lessons" is. Counted from the
            * store on every change, never written next to a control, where it
            * would go stale the first time a lesson is added.
            */}
          Open without paying — {openSet.size} of {totalCount} lessons
        </h3>
        <p className="general__section-hint">
          Tick a whole course, or open it a lesson at a time. Saved here, it applies on the next
          request — nothing is built and nothing is deployed. Everything unticked stays behind the
          paywall, and so does everything if this document is ever unreadable: the server opens
          nothing when in doubt.
        </p>

        {courses.length === 0 ? (
          <p className="general__section-hint">No courses in this project yet — nothing to open.</p>
        ) : (
          courses.map((c) => {
            const whole = pickedCourses.includes(c.slug);
            const openHere = c.lessons.filter((l) => openSet.has(l.slug)).length;
            const isOpen = expanded.includes(c.slug);
            return (
              <div key={c.slug} className="general__section">
                <label
                  className={`cms-modal__radio free-access__row ${whole ? "cms-modal__radio--active" : ""}`}
                >
                  {/*
                    * THE TICK IS DRAWN, NOT RELIED UPON. This stylesheet hides
                    * the native input inside .cms-modal__radio, so the only cue
                    * a checked row had was a pale background — unreadable in a
                    * list where many rows are ticked, and invisible for a
                    * disabled one. The box is rendered here so the state can be
                    * seen rather than inferred from colour alone.
                    */}
                  <input type="checkbox" checked={whole} onChange={() => toggleCourse(c.slug)} />
                  <span className={`free-access__tick ${whole ? "free-access__tick--on" : ""}`}>
                    {whole ? "✓" : ""}
                  </span>
                  <span className="free-access__text">
                    <span className="cms-modal__radio-label">{c.title}</span>
                    <span className="cms-modal__radio-hint">
                      {openHere} of {c.lessons.length} open
                    </span>
                  </span>
                </label>

                {c.lessons.length > 0 && (
                  <button
                    type="button"
                    className="cms__subcat-add-btn"
                    onClick={() => toggleExpand(c.slug)}
                  >
                    {isOpen ? "Hide lessons" : `Choose lessons (${c.lessons.length})`}
                  </button>
                )}

                {isOpen &&
                  c.lessons.map((l) => {
                    const named = pickedLessons.includes(l.slug);
                    return (
                      <label
                        key={l.slug}
                        className={`cms-modal__radio free-access__row free-access__row--lesson ${
                          openSet.has(l.slug) ? "cms-modal__radio--active" : ""
                        } ${whole ? "free-access__row--fixed" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={named || whole}
                          disabled={whole}
                          onChange={() => toggleLesson(l.slug)}
                        />
                        <span
                          className={`free-access__tick ${openSet.has(l.slug) ? "free-access__tick--on" : ""}`}
                        >
                          {openSet.has(l.slug) ? "✓" : ""}
                        </span>
                        <span className="free-access__text">
                        <span className="cms-modal__radio-label">{l.title}</span>
                        {/*
                          * WHY A TICK CAN BE ON AND UNTOUCHABLE. With the whole
                          * course open, every lesson in it is open whatever this
                          * box says — showing it unticked would be a lie, and
                          * letting it be cleared would promise a closing that
                          * does not happen. The reason is said rather than left
                          * to be guessed from a grey box.
                          */}
                        <span className="cms-modal__radio-hint">
                          {whole ? "open with the whole course" : named ? "open" : "locked"}
                        </span>
                        </span>
                      </label>
                    );
                  })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FreeAccessSettings;
